import React, {useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import AuthService from '../core/_requests'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {ValidationErrorModal} from '../../../../_gori/partials/widgets'
import {useFormik} from 'formik'
import {Button, InputTextFormik} from '../../../../_gori/partials/widgets'
import {StorageHelpers} from '../../../../_gori/helpers'
import * as Yup from 'yup'
import HttpStatusCode from '../../../api/HttpStatusCode'

type Props = {
  show: boolean
  data: any
  handleClose: () => void
  phone: string
  fromPage: string
  verifiedPhone: boolean
  updateVerifiedPhone: () => void
  triggerSubmitForm?: () => void
}

const VerificationPhoneModal: React.FC<Props> = ({
  show,
  data,
  handleClose,
  phone,
  fromPage,
  verifiedPhone,
  updateVerifiedPhone,
  triggerSubmitForm,
}) => {
  const intl = useIntl()
  const {newCancelToken} = useCancelToken()
  const initialOtpTime = 600
  const [validationErrors, setValidationErrors] = useState<any>()
  const [message, setMessage] = useState<{
    otp: string | boolean
    resend: string | boolean
  }>({
    otp: false,
    resend: false,
  })
  const [time, setTime] = useState<{
    otp: number
    resend: number
    verify: number
  }>({
    otp: initialOtpTime,
    resend: data.retry_after,
    verify: 0,
  })
  const [isResendingOTP, setIsResendingOTP] = useState(false)
  const [attemptsExceeded, setAttemptsExceeded] = useState(false)
  const [sendCodeCount, setSendCodeCount] = useState({
    send_code_count: data.send_code_count || 1,
    max_send: data.max_send || 3,
  })
  const isFromRegistrationPage = fromPage === 'registration'

  const resendOtp = useCallback(async () => {
    setIsResendingOTP(true)
    const payload = {
      phone: phone,
    }
    const config = {cancelToken: newCancelToken()}
    await AuthService.sendOTP(payload, config)
      .then((response) => {
        setTime((prev) => ({...prev, otp: initialOtpTime}))
        setMessage((prev) => ({
          ...prev,
          otp: false,
          responseSend2faCode: intl.formatMessage({id: 'MESSAGE_RESEND'}),
        }))
        setAttemptsExceeded(false)
        if (response.send_code_count && response.max_send) {
          setSendCodeCount({
            send_code_count: response.send_code_count,
            max_send: response.max_send,
          })
        }
        verifyOtp.resetForm()
      })
      .catch((error: any) => {
        if (error?.response?.status === HttpStatusCode.TOO_MANY_REQUESTS) {
          const errorData = error?.response?.data?.error
          if (errorData?.fields?.max_send_reached) {
            setMessage((prev) => ({
              ...prev,
              otp: errorData?.message || intl.formatMessage({id: 'MAX_OTP_ATTEMPTS_REACHED'}),
            }))
          }
        } else {
          setValidationErrors(error?.response)
        }
        setMessage((prev) => ({...prev, responseSend2faCode: false}))
      })
      .finally(() => {
        setIsResendingOTP(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl, newCancelToken, phone])

  const validationSchema = Yup.object().shape({
    otp: Yup.array().of(
      Yup.string().matches(/^[0-9]$/, intl.formatMessage({id: 'OTP_MUST_BE_NUMBER'}))
    ),
  })

  const verifyOtp = useFormik({
    initialValues: {otp: ['', '', '', '', '', '']},
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, {setSubmitting}) => {
      const filledCount = values.otp.filter((item) => item !== '').length
      if (filledCount === 0) {
        setMessage((prev) => ({...prev, otp: intl.formatMessage({id: 'OTP_REQUIRED'})}))
        return
      } else if (filledCount < 6) {
        setMessage((prev) => ({...prev, otp: intl.formatMessage({id: 'OTP_6_DIGIT'})}))
        return
      }

      setSubmitting(true)
      const config = {cancelToken: newCancelToken()}
      const payload = {
        code: values.otp.join(''),
        phone: phone,
      }
      await AuthService.verifyOTP(payload, config)
        .then((response) => {
          if (response.status) {
            updateVerifiedPhone()
            StorageHelpers.setItemLocalStorage('PHONE_VERIFIED', phone)
            if (isFromRegistrationPage) {
              triggerSubmitForm?.()
            }
          } else {
            if (Number(response.attempts_remaining) <= 0) {
              setMessage((prev) => ({
                ...prev,
                otp: intl.formatMessage({id: 'MAX_OTP_ATTEMPTS_REACHED'}),
                responseSend2faCode: false,
              }))
              setAttemptsExceeded(true)
              return
            }
            setMessage((prev) => ({
              ...prev,
              otp: intl.formatMessage(
                {id: 'VERIFY_FAILED'},
                {attempts_remaining: response.attempts_remaining || 0}
              ),
              responseSend2faCode: false,
            }))
            verifyOtp.resetForm()
          }
        })
        .catch((error: any) => {
          const errorData = error?.response?.data?.error
          if (error?.response?.status === HttpStatusCode.TOO_MANY_REQUESTS) {
            setMessage((prev) => ({
              ...prev,
              otp: errorData?.message,
              responseSend2faCode: false,
            }))
            return
          }
          if (errorData?.fields?.attempts_exceeded) {
            setMessage((prev) => ({
              ...prev,
              otp: intl.formatMessage({id: 'MAX_OTP_ATTEMPTS_REACHED'}),
              responseSend2faCode: false,
            }))
            setAttemptsExceeded(true)
          } else {
            setValidationErrors(error?.response)
          }
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
  })

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = time % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [])

  const focusToNextOTP = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputName = event?.target?.name
      const match = inputName.match(/\[(\d+)]/)
      const index = match ? parseInt(match[1], 10) : null

      if (index === null) return

      // Check if all 6 OTP fields are filled after this input
      const currentValues = [...verifyOtp.values.otp]
      currentValues[index] = event.target.value

      const allFilled = currentValues.every((value) => value !== '')

      if (allFilled) {
        // Auto-submit when all 6 digits are entered
        setTimeout(() => {
          verifyOtp.submitForm()
        }, 100)
        return
      }

      // Otherwise, focus to next input if not the last one
      if (index < 5) {
        const nextOtpInput = document.querySelector<HTMLInputElement>(
          `input[name="otp[${index + 1}]"]`
        )
        if (nextOtpInput) {
          nextOtpInput.focus()
        }
      }
    },
    [verifyOtp]
  )

  const handlePasteOTP = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text')
      const digits = pastedData.replace(/\D/g, '').slice(0, 6)

      if (digits.length === 6) {
        const newOtpValues = digits.split('')
        verifyOtp.setFieldValue('otp', newOtpValues)

        // Auto-submit after paste
        setTimeout(() => {
          verifyOtp.submitForm()
        }, 100)
      }
    },
    [verifyOtp]
  )

  const maskPhoneNumber = (phone: string, lastDigits: number = 4) => {
    const digits = phone.replace(/\D/g, '')
    if (digits.length <= lastDigits) return digits

    const last4 = digits.slice(-lastDigits)
    return `***-***-${last4}`
  }
  const ContactSupportLink = () => (
    <a
      href='/support'
      className='text-primary text-decoration-underline fw-bold'
      target='_blank'
      rel='noopener noreferrer'
    >
      {intl.formatMessage({id: 'CONTACT_SUPPORT'})}
    </a>
  )

  const ResendCodeButton = () => (
    <Button
      className='btn btn-advanced-primary'
      label={intl.formatMessage({id: 'RESEND_CODE'})}
      loadingText={intl.formatMessage({id: 'SENDING'})}
      event={resendOtp}
      disabled={isResendingOTP}
      loading={isResendingOTP}
    />
  )

  useEffect(() => {
    if (data.whitelisted && isFromRegistrationPage) {
      triggerSubmitForm?.()
    }
  }, [data.whitelisted, isFromRegistrationPage, triggerSubmitForm])

  useEffect(() => {
    if (data.send_code_count || data.max_send) {
      setSendCodeCount({
        send_code_count: data.send_code_count || 1,
        max_send: data.max_send || 3,
      })
    }
  }, [data.send_code_count, data.max_send])

  useEffect(() => {
    let timer
    if (time.otp > 0) {
      timer = setInterval(() => {
        setTime((prev) => ({...prev, otp: prev.otp - 1}))
      }, 1000)
    } else if (time.otp === 0) {
      setMessage((prev) => ({...prev, otp: false}))
    }
    return () => clearInterval(timer)
  }, [time.otp])

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <Modal
        id='gori_modal_phone_verify'
        tabIndex={-1}
        centered
        dialogClassName='modal-size-md h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleClose}
      >
        <Modal.Header closeButton={!verifiedPhone || !isFromRegistrationPage}>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'VERIFY_CODE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='vh-75'>
          {(data.whitelisted || verifiedPhone) && (
            <div>
              <h2 className='text-center my-5'>{intl.formatMessage({id: 'VERIFIED'})}</h2>
              <div className='d-flex justify-content-center my-5'>
                <svg
                  width='120'
                  height='120'
                  viewBox='0 0 120 120'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M101.367 58.4731C101.367 61.2015 97.4389 63.4956 96.9124 66.0684C96.3859 68.6412 99.1151 72.2112 98.1151 74.5754C97.1152 76.9396 92.6647 77.4353 91.2442 79.5379C89.8237 81.6405 91.0387 85.9357 89.2507 87.7237C87.4626 89.5117 83.1674 88.2968 81.0648 89.7173C78.9622 91.1378 78.4666 95.5883 76.1023 96.5882C73.7381 97.5882 70.1681 94.8591 67.5953 95.3855C65.0225 95.912 62.7284 99.8398 60 99.8398C57.2716 99.8398 54.9775 95.912 52.4047 95.3855C49.8319 94.859 46.2619 97.5882 43.8977 96.5882C41.5335 95.5883 41.0378 91.1378 38.9352 89.7173C36.8326 88.2968 32.5374 89.5117 30.7493 87.7237C28.9613 85.9357 30.1763 81.6405 28.7558 79.5379C27.3353 77.4353 22.8848 76.9397 21.8849 74.5754C20.8849 72.2112 23.6141 68.6412 23.0876 66.0684C22.5611 63.4956 18.6333 61.2015 18.6333 58.4731C18.6333 55.7446 22.5611 53.4505 23.0875 50.8777C23.614 48.3049 20.8849 44.7349 21.8848 42.3708C22.8848 40.0066 27.3352 39.5109 28.7557 37.4083C30.1762 35.3058 28.9613 31.0105 30.7493 29.2225C32.5373 27.4344 36.8326 28.6494 38.9351 27.2289C41.0377 25.8084 41.5334 21.358 43.8976 20.358C46.2618 19.3581 49.8318 22.0872 52.4046 21.5607C54.9774 21.0342 57.2715 17.1064 59.9999 17.1064C62.7284 17.1064 65.0225 21.0342 67.5953 21.5607C70.1681 22.0872 73.7381 19.3581 76.1023 20.358C78.4664 21.358 78.9621 25.8084 81.0647 27.2289C83.1674 28.6494 87.4625 27.4345 89.2506 29.2225C91.0387 31.0105 89.8236 35.3057 91.2441 37.4083C92.6646 39.5109 97.1151 40.0066 98.1151 42.3708C99.115 44.7349 96.3859 48.3049 96.9124 50.8777C97.4389 53.4505 101.367 55.7446 101.367 58.4731Z'
                    fill='#14B8A6'
                  />
                  <path
                    d='M87.128 43.0604L81.9047 37.8378C81.1403 37.0735 79.9011 37.0736 79.1368 37.8379L52.465 64.5107L40.8633 52.9091C40.099 52.1448 38.8598 52.1447 38.0954 52.909L32.8722 58.1316C32.1077 58.8959 32.1077 60.1353 32.8721 60.8997L51.081 79.1089C51.8454 79.8732 53.0846 79.8732 53.8489 79.1088L87.128 45.8285C87.8924 45.064 87.8924 43.8247 87.128 43.0604Z'
                    fill='white'
                  />
                </svg>
              </div>
            </div>
          )}

          {!data.whitelisted && !verifiedPhone && (
            <div>
              <h2 className='text-xl font-semibold mb-4 text-center'>
                {intl.formatMessage({id: 'VERIFY_CODE'})}
              </h2>
              <div>
                <p className='text-center'>
                  {intl.formatMessage(
                    {id: 'ENTER_THE_CODE_SENT_TO_PHONE_NUMBER'},
                    {phoneNumber: maskPhoneNumber(phone)}
                  )}
                </p>
              </div>
              <div className='justify-content-center d-flex'>
                <p className='mb-4 me-2'>{intl.formatMessage({id: 'REMAINING'})}</p>
                <span className='text-primary'>{formatTime(time.otp)}</span>
              </div>

              {/* OTP inputs - always show but disable when expired */}
              <div className='mt-4 mb-10 mx-10'>
                <div className='d-flex w-75 m-auto'>
                  {Array.from({length: 6}).map((_, index) => (
                    <InputTextFormik
                      key={index}
                      type='text'
                      name={`otp[${index}]`}
                      maxlength={1}
                      formik={verifyOtp}
                      className='mx-1 otp'
                      onChange={(event: any) => focusToNextOTP(event)}
                      onPaste={handlePasteOTP}
                      disabled={time.otp === 0 || verifyOtp.isSubmitting}
                    />
                  ))}
                </div>
              </div>

              {/* Show expired message or other error messages */}
              {time.otp === 0 ? (
                <div className='text-danger text-center'>
                  {intl.formatMessage({id: 'OTP_EXPIRED'})}
                </div>
              ) : (
                message.otp && <div className='text-danger text-center'>{message.otp}</div>
              )}
              {message.resend && <div className='text-primary text-center'>{message.resend}</div>}
            </div>
          )}
        </Modal.Body>

        {!data.whitelisted && !verifiedPhone && (
          <Modal.Footer>
            {/* When OTP expired - show Contact Support + Resend Code */}
            {time.otp === 0 ? (
              <>
                <div className='d-flex justify-content-start flex-grow-1'>
                  <ContactSupportLink />
                </div>
                <div className='d-flex justify-content-end'>
                  <ResendCodeButton />
                </div>
              </>
            ) : attemptsExceeded ? (
              <>
                {sendCodeCount.send_code_count < sendCodeCount.max_send ? (
                  <>
                    <div className='d-flex justify-content-start flex-grow-1'>
                      <ContactSupportLink />
                    </div>
                    <div className='d-flex justify-content-end'>
                      <ResendCodeButton />
                    </div>
                  </>
                ) : (
                  <div className='d-flex justify-content-center w-100'>
                    <ContactSupportLink />
                  </div>
                )}
              </>
            ) : (
              /* Normal state: Show regular buttons */
              <>
                <div className='d-flex justify-content-start flex-grow-1'>
                  {sendCodeCount.send_code_count < sendCodeCount.max_send ? (
                    <Button
                      className='btn btn-link text-decoration-underline p-0 text-primary'
                      label={intl.formatMessage({id: 'DIDNT_RECEIVE_CODE'})}
                      loadingText={intl.formatMessage({id: 'SENDING'})}
                      event={resendOtp}
                      disabled={verifyOtp.isSubmitting || isResendingOTP}
                      loading={isResendingOTP}
                    />
                  ) : (
                    <ContactSupportLink />
                  )}
                </div>

                <div className='d-flex justify-content-end'>
                  <Button
                    className='btn btn-advanced-secondary'
                    label={intl.formatMessage({id: 'CLOSE'})}
                    loadingText={intl.formatMessage({id: 'CLOSE'})}
                    event={handleClose}
                    disabled={verifyOtp.isSubmitting || isResendingOTP}
                  >
                    {verifyOtp.isSubmitting ? (
                      <span className='spinner-border spinner-border-sm me-2' />
                    ) : null}
                  </Button>
                </div>
              </>
            )}
          </Modal.Footer>
        )}
      </Modal>
    </>
  )
}

export {VerificationPhoneModal}
